import { DndContext } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import type { GridColDef, GridColumnGroup, GridColumnVisibilityModel } from '@repo/go-table';
import { Drawer, Scrollbar } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { FIELD_TO_COLUMN_TITLE_KEY } from '../alert-rules-table/FIELD_TO_COLUMN_TITLE_KEY';
import { FIELD_TO_GROUP_TITLE_KEY } from '../alert-rules-table/FIELD_TO_GROUP_TITLE_KEY';
import type { DisplayManager } from '../alert-rules-table/services/DisplayManager';
import { MIN_VISIBLE_COLUMNS, SERVICE_COLUMNS } from '../alert-rules-table/services/DisplayManager';
import type { TAlertRule } from '../types';

import { DisplaySettingItem } from './DisplaySettingItem';

type Props = {
  displayManager: DisplayManager;
  columns: GridColDef[];
  groups: GridColumnGroup[];
  open: boolean;
  onClose: () => void;
  container?: HTMLDivElement;
};

const HIDDEN_IN_SETTINGS_SIDEBAR_COLUMNS = [...SERVICE_COLUMNS];

export const AlertRulesTableSettings = observer<Props>(function AlertRulesTableSettings({
  displayManager,
  columns,
  groups,
  open,
  onClose,
  container,
}) {
  const { t } = useTranslation();

  const { leftPinned, free, rightPinned, orderedColumns } = useMemo(() => {
    const { left, right } = displayManager.settings.pinnedColumns ?? {};

    const { leftPinned, free, rightPinned } = columns.reduce(
      (
        acc: {
          leftPinned: GridColDef[];
          free: GridColDef[];
          rightPinned: GridColDef[];
        },
        col,
      ) => {
        if (left?.includes(col.field)) {
          acc.leftPinned.push(col);
          return acc;
        }

        if (right?.includes(col.field)) {
          acc.rightPinned.push(col);
          return acc;
        }

        acc.free.push(col);

        return acc;
      },
      {
        leftPinned: [],
        free: [],
        rightPinned: [],
      },
    );

    const columnsOrderModel = displayManager.settings.columnsOrderModel;

    if (columnsOrderModel?.length) {
      free.sort((a, b) => {
        const c1index = columnsOrderModel.indexOf(a.field);
        const c2index = columnsOrderModel.indexOf(b.field);

        if (c1index < 0 && c2index < 0) {
          return 0;
        }

        if (c1index < 0) {
          return -1;
        }

        if (c2index < 0) {
          return 1;
        }

        return c1index - c2index;
      });
    }

    return { leftPinned, free, rightPinned, orderedColumns: [...leftPinned, ...free, ...rightPinned] };
  }, [columns, displayManager.settings.columnsOrderModel, displayManager.settings.pinnedColumns]);

  const handleColumnVisibilityChange = (column: string, value: boolean) => {
    const { settings } = displayManager;

    const newVisibilityModel: GridColumnVisibilityModel = {
      ...settings.columnsVisibility,
      [column]: value,
    };

    displayManager.onColumnVisibilityChange(newVisibilityModel);
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { settings } = displayManager;

      const savedColumnsOrderModel = settings?.columnsOrderModel ?? [];
      const columnsOrderModel = [...columns.map((c) => c.field)].sort((a, b) => {
        const c1index = savedColumnsOrderModel.indexOf(a);
        const c2index = savedColumnsOrderModel.indexOf(b);

        if (c1index < 0 && c2index < 0) {
          return 0;
        }

        if (c1index < 0) {
          return -1;
        }

        if (c2index < 0) {
          return 1;
        }

        return c1index - c2index;
      });

      const activeField = event.active.id;
      const overField = event.over?.id;

      if (overField == null || activeField == null) {
        return;
      }

      const newIndex = columnsOrderModel.findIndex((item) => item === overField);
      const oldIndex = columnsOrderModel.findIndex((item) => item === activeField);

      const reorderedColumns = arrayMove(columnsOrderModel, oldIndex, newIndex);

      displayManager.onColumnsOrderModelChange(reorderedColumns);
    },
    [columns, displayManager],
  );

  const checkIsColumnVisibilityDisabled = (columnField: string): boolean => {
    const { settings } = displayManager;

    if (!settings.columnsOrderModel) return false;

    const visibleColumns = settings.columnsVisibility;

    if (!visibleColumns) return false;

    const isColumnVisible = visibleColumns[columnField] === true || visibleColumns[columnField] === undefined;
    let visibleColumnsCount = 0;

    settings.columnsOrderModel.forEach((item) => {
      if (visibleColumns[item] === true || visibleColumns[item] === undefined) {
        visibleColumnsCount += 1;
      }
    });

    const columnDef = columns.find((column) => column.field === columnField);

    if (!columnDef) return true;

    if (columnDef.hideable === false) {
      return true;
    } else {
      if (!isColumnVisible) {
        return false;
      }

      return visibleColumnsCount <= MIN_VISIBLE_COLUMNS;
    }
  };

  const renderItems = (renderedColumns: GridColDef[], isPinned = false): ReactNode => {
    return renderedColumns.map((column) => {
      if (HIDDEN_IN_SETTINGS_SIDEBAR_COLUMNS.includes(column.field)) {
        return null;
      }

      const groupId =
        groups.find((g) => g.children.find((c) => 'field' in c && c.field === column.field))?.groupId ?? null;
      const fieldName = t(FIELD_TO_COLUMN_TITLE_KEY[column.field as keyof TAlertRule]);
      const groupName = groupId ? t(FIELD_TO_GROUP_TITLE_KEY[groupId as keyof TAlertRule]) : null;

      const title = `${groupName ? groupName + ' /' : ''} ${fieldName}`;

      return (
        <DisplaySettingItem
          key={column.field}
          field={column.field}
          isPinned={isPinned}
          isDragDisabled={isPinned}
          title={title}
          isVisibilitySwitcherDisabled={checkIsColumnVisibilityDisabled(column.field)}
          isVisible={
            displayManager.settings.columnsVisibility?.[column.field] === undefined
              ? true
              : displayManager.settings.columnsVisibility[column.field]
          }
          onColumnVisibilityChange={handleColumnVisibilityChange}
        />
      );
    });
  };

  return (
    <Drawer
      open={open}
      onClose={() => onClose()}
      style={{ width: 320 }}
      title={t('alertsManagerWidget:tableSettings')}
      container={container}
    >
      <Scrollbar>
        {renderItems(leftPinned, true)}
        <DndContext modifiers={[restrictToVerticalAxis, restrictToParentElement]} onDragEnd={handleDragEnd}>
          <SortableContext items={orderedColumns.map((col) => col.field)}>
            <div>{renderItems(free)}</div>
          </SortableContext>
        </DndContext>
        {renderItems(rightPinned, true)}
      </Scrollbar>
    </Drawer>
  );
});
